/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { faExchange } from "@fortawesome/pro-regular-svg-icons";
import classnames from "classnames";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
    Col,
    FormGroup, FormText,
    Input,
    Label,
    Modal, ModalBody, ModalFooter, ModalHeader,
    Nav, NavItem, NavLink,
    Row,
    TabContent, TabPane
} from "reactstrap";
import { getTenant, listTenants, resetAllTenantConfig, resetAllTenantDefect, resetAllTenantFacilityLocation, resetAllTenantFieldOption, updateTenant, updateTenantState } from "../../../actions/Tenants/actions";
import useConfig, { useEDDateFormats } from "../../../actions/Tenants/config/configHook";
import { CONFIG_TYPES } from "../../../actions/Tenants/config/constants";
import { AvosAction } from "../../../actions/types";
import { getLocalHistoryRoute, useLocalHistory } from "../../../context/LocalHistory";
import { toast } from "../../../utils/toast";
import useNavigateFrom from "../../../utils/useNavigateFrom";
import { Breadcrumbs } from "../../Helper/Breadcrumbs";
import { Button } from "../../Helper/Button";
import { FormContainer } from "../../Helper/FormContainer";
import { Loading } from "../../Helper/Loading";
import { formatDateForFilename, formatDateTime } from "../Layers/fields/formatters";
import FullPageLayout from "../Layout/FullPageLayout";
import { defaultCheckLocation } from "./Forms/CheckLocationConfigForm";
import { defaultFruitType } from "./Forms/defaultFruitType";
import { defaultLayerType } from "./Forms/LayerTypeConfigForm";
import { defaultStage } from "./Forms/OverviewTabConfigForm";
import { defaultPdfReport } from "./Forms/PDFReportConfigForm";
import { defaultRootConfig } from "./Forms/RootConfigForm";
import ListTenantConfig from "./ListTenantConfig";
import ListTenantFormOptions from "./ListTenantFormOptions";
import ListTenantInformation from "./ListTenantInformation";
import TenantPageWrappedInConfigProvider from "./TenantPageWrappedInConfigProvider";

// * Wrap this page in the config of the tenant we are editing
function WrappedInConfigProviderTenantEditForm() {
    return <TenantPageWrappedInConfigProvider>
        <TenantEditForm />
    </TenantPageWrappedInConfigProvider>;
}

export default WrappedInConfigProviderTenantEditForm;

const tabList = [
    { name: "Root", key: "root", tab: () => <ListTenantConfig title="Root" type={CONFIG_TYPES.ROOT} defaultConfig={defaultRootConfig} /> }, // eslint-disable-line react/display-name
    { name: "Information", key: "information", tab: () => <ListTenantInformation title="Information" /> }, // eslint-disable-line react/display-name
    { name: "Form options", key: "form-options", tab: () => <ListTenantFormOptions title="Form options" /> }, // eslint-disable-line react/display-name
    { name: "Overview Tabs", key: CONFIG_TYPES.STAGE, tab: (props) => <ListTenantConfig title="Overview tabs" type={CONFIG_TYPES.STAGE} defaultConfig={defaultStage} {...props} /> }, // eslint-disable-line react/display-name
    { name: "Layer types", key: CONFIG_TYPES.LAYER_TYPE, tab: (props) => <ListTenantConfig title="Layer types" type={CONFIG_TYPES.LAYER_TYPE} defaultConfig={defaultLayerType} {...props} /> }, // eslint-disable-line react/display-name
    { name: "Check locations", key: CONFIG_TYPES.CHECK_LOCATION, tab: () => <ListTenantConfig title="Check locations" type={CONFIG_TYPES.CHECK_LOCATION} defaultConfig={defaultCheckLocation} /> }, // eslint-disable-line react/display-name
    { name: "Fruits", key: CONFIG_TYPES.FRUIT_TYPE, tab: () => <ListTenantConfig title="Fruit type" type={CONFIG_TYPES.FRUIT_TYPE} defaultConfig={defaultFruitType} /> }, // eslint-disable-line react/display-name
    { name: "Reports", key: CONFIG_TYPES.PDF_REPORT, tab: () => <ListTenantConfig title="PDF Report" type={CONFIG_TYPES.PDF_REPORT} defaultConfig={defaultPdfReport} /> }, // eslint-disable-line react/display-name
    { name: "Color Code", key: CONFIG_TYPES.COLOR_CODE, tab: () => <ListTenantConfig title="Color Code Schemes" type={CONFIG_TYPES.COLOR_CODE} /> }, // eslint-disable-line react/display-name
] as { name: string; key: string; tab: (props?) => JSX.Element}[];

const TenantEditForm = () => {
    const current = useSelector((state: any) => state.tenants.current);
    const isLoading = useSelector((state: any) => state.tenants.isLoading);
    const isUpdating = useSelector((state: any) => state.tenants.isUpdating);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { navigateTo } = useNavigateFrom();
    const params = useParams();
    const [importModal, setImportModal] = useState(false);
    const edFormats = useEDDateFormats();

    const activeTab: string = params?.config_tab || tabList[0].key;
    const activeTabObj = useMemo(() => tabList.find((t) => t.key === activeTab) || tabList[0], [activeTab]);

    const localHistory = useLocalHistory();
    const backTo = useMemo(() => {
        const lastRoute = localHistory[localHistory.length - 1];
        // check if user came from a route outside of tenants
        return !lastRoute.startsWith("/tenants") ? lastRoute : getLocalHistoryRoute(localHistory, "/tenants/");
    }, [localHistory]);

    const {
        tenant_name,
        created,
    } = current;
    const { tenant_id } = params;

    useEffect(() => {
        if (tenant_id) {
            dispatch(getTenant(tenant_id) as AvosAction);
        }

    }, [tenant_id]);

    const onChange = (e) => {
        dispatch(updateTenantState({ ...current, [e.target.name]: e.target.value }));
    };

    const onSubmit = () => {
        dispatch(updateTenant(current) as any).then(() => dispatch(listTenants() as AvosAction));
    };

    const doSwitchTenant = (path = "/") => navigate(`/${current.tenant_id}${path}`);

    const toggleTab = (tab) => {
        if (activeTab !== tab) {
            navigateTo(`/tenants/${tenant_id}/tab/${tab.key}`);
        }
    };

    const buttons = (
        <>
            <Button color="secondary" className="text-nowrap" outline onClick={() => doSwitchTenant("/admin")}>Admin Portal</Button>
            <Button color="secondary" className="text-nowrap" outline disabled={isUpdating || isLoading} onClick={() => doSwitchTenant("/")}>Test</Button>
            <Button color="secondary" className="text-nowrap" outline onClick={() => setImportModal(true)} icon={faExchange}>Import / Export </Button>
        </>
    );

    if (!current || (isLoading && current.tenant_id !== tenant_id)) {
        return <Loading />;
    }

    return (
        <FullPageLayout>
            <FormContainer
                title={`Edit tenant ${tenant_name}`}
                backTo={backTo}
                actionsTop={buttons}
                actionsBottom={buttons}
                breadcrumbs={<Breadcrumbs items={[{ label: "Tenants", url: "/tenants", isActive: false }, { label: tenant_name, url: `/tenants/${tenant_id}`, isActive: true }]} />}
            >
                <Row>
                    <Col xs="12">
                        <FormGroup>
                            <Label for="tenant_name">Tenant name</Label>
                            <Input type="text" name="tenant_name" id="tenant_name" disabled value={tenant_name} onChange={onChange} />
                        </FormGroup>
                    </Col>
                    <Col md="6">
                        <FormGroup>
                            <Label for="tenant_id">Tenant ID</Label>
                            <Input type="text" name="tenant_id" id="tenant_id" disabled value={tenant_id} onChange={onChange} />
                        </FormGroup>
                    </Col>
                    <Col md="6">
                        <FormGroup>
                            <Label for="created">Created</Label>
                            <Input type="text" disabled name="created" id="created" value={formatDateTime(created, edFormats.datetime_format)} />
                        </FormGroup>
                    </Col>
                </Row>

                <Nav tabs>
                    {tabList.map((tab) => <NavItem key={tab.key} >
                        <NavLink
                            className={classnames({ active: activeTab === tab.key })}
                            onClick={() => toggleTab(tab) }>
                            {tab.name}
                        </NavLink>
                    </NavItem>)}
                </Nav>

                <TabContent activeTab={activeTab}>
                    <TabPane tabId={activeTab}>
                        <Row>
                            <Col sm="12">
                                {activeTabObj.tab()}

                                {activeTab === "information" && (
                                    <div css={css`justify-self: flex-end;`}>
                                        <Button color="primary" disabled={isUpdating || isLoading} onClick={onSubmit}>Update tenant</Button>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </TabPane>
                </TabContent>
            </FormContainer>

            <TenantImportExportModal importModal={importModal} setImportModal={setImportModal} />
        </FullPageLayout>
    );
};

const exportData = (data, fileName, type) => {
    // Create a link and download the file
    const blob = new Blob([data], { type });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
};

function TenantImportExportModal({ importModal, setImportModal }) {
    const [importData, setImportData] = useState<any>();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const isLoading = useSelector((state: any) => state.tenants.isLoading);
    const isUpdating = useSelector((state: any) => state.tenants.isUpdating);
    const tenant = useSelector((state: any) => state.tenants.current);
    const options_by_field = useSelector((state: any) => state?.tenants?.options_by_field);
    const defects_by_fruit_type = useSelector((state: any) => state?.tenants?.defects_by_fruit_type);
    const facility_locations = useSelector((state: any) => state?.tenants?.locations);
    // const configs = useSelector((state) => state?.tenants?.configs);
    const dispatch = useDispatch();
    const config = useConfig();
    const edFormats = useEDDateFormats();

    const [fileSelectorValue, setFileSelectorValue] = useState("");
    const fileChangeHandler = (e) => {
        const file = e.target.files[0];

        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const jsonData = JSON.parse(event?.target?.result as string || "{}");
                    setImportData(jsonData);
                    setSelectedFile(file);
                } catch (error) {
                    toast.error(`Error parsing JSON file: ${error}`, {
                        autoClose: 1500,
                    });
                }
            };
            setFileSelectorValue(e.target.value);
            reader.readAsText(file);
        }
    };

    const exportOptions = () => {
        const form_options = options_by_field[tenant.tenant_id];
        const defects = defects_by_fruit_type[tenant.tenant_id];
        const { configs } = config;

        const data = {
            tenant,
            form_options: Object.keys(form_options).flatMap((field) => form_options[field]),
            configs,
            defects: Object.keys(defects).flatMap((fruit_type) => defects[fruit_type]),
            facility_locations
        };

        const jsonData = JSON.stringify(data, null, 2);

        exportData(jsonData, `export-${tenant.tenant_name}-${formatDateForFilename(edFormats.datetime_format)}-all.json`, "application/json;charset=utf-8;");
    };

    const handleImport = () => {
        if (!importData) return;

        const promises = [
            dispatch(updateTenant(importData.tenant) as any),
            dispatch(resetAllTenantFieldOption(importData.tenant.tenant_id, importData.form_options) as AvosAction)
        ];

        if (importData.configs) {
            promises.push(dispatch(resetAllTenantConfig(importData.tenant.tenant_id, importData.configs) as any));
        }

        if (importData.defects) {
            promises.push(dispatch(resetAllTenantDefect(importData.tenant.tenant_id, importData.defects) as AvosAction));
        }

        if (importData.facility_locations) {
            promises.push(dispatch(resetAllTenantFacilityLocation(importData.tenant.tenant_id, importData.facility_locations) as AvosAction));
        }

        Promise.all(promises).then(() => {
            setImportModal(false);
        });

    };

    return <Modal size="lg" isOpen={importModal} toggle={() => setImportModal(false)} >
        <ModalHeader toggle={() => setImportModal(false)} >Import / Delete all</ModalHeader>
        <ModalBody>
            <p>This will either import or export all tenant settings, including name, power bi settings, fields, form fields, form options and legacy settings. <b>An import will remove all current fields and forms!</b> Use with care.</p>
            <FormGroup>
                <Label for="exampleFile">JSON</Label>
                <Input
                    id="exampleFile"
                    name="file"
                    type="file"
                    value={fileSelectorValue}
                    onChange={fileChangeHandler}
                />

                {selectedFile ? (
                    <div className="py-3">
                        <FormText >
                            <div>Filename: {selectedFile.name}</div>
                            <div>Filetype: {selectedFile.type}</div>
                            <div>Size in bytes: {selectedFile.size}</div>
                            <div>Configs: <b>{importData && importData?.configs?.length}</b></div>
                            <div>Form Options: <b>{importData && importData?.form_options?.length}</b></div>
                            <div>Defects: <b>{importData && importData?.defects?.length}</b></div>
                            <div>Facility locations: <b>{importData && importData?.facility_locations?.length}</b></div>
                        </FormText>
                    </div>

                ) : (<FormText>Upload json file here.</FormText>)}
            </FormGroup>
            {importData && importData.tenant.tenant_id !== tenant.tenant_id && <div className="alert alert-danger">The tenant id of the import file does not match the current tenant id. This is probably a bad idea. <br></br>
                import {importData.tenant.tenant_id} ({importData.tenant.tenant_name}) !== {tenant.tenant_id} ({tenant.tenant_name})  </div>}
            {importData && <div css={css`max-height: 70vh;overflow-y:scroll;`}>
                <pre>
                    {JSON.stringify(importData, null, 2)}
                </pre>
            </div>
            }

        </ModalBody>
        <ModalFooter>
            <Button color="light" onClick={() => setImportModal(false)}>Cancel</Button>
            <Button color="light" className="me-1 mb-1" onClick={exportOptions}>Export</Button>
            <Button color="primary" disabled={isLoading || isUpdating || !importData} className="me-1 mb-1" onClick={handleImport}>Import</Button>
        </ModalFooter>
    </Modal>;
}

TenantImportExportModal.propTypes = {
    importModal: PropTypes.bool.isRequired,
    setImportModal: PropTypes.func.isRequired
};
